// Two-step OTP login gate for gated (team-visibility) shares.
// Renders email input → "Get code" → code input → "Sign in".
// Calls `onAuthed()` after successful verify.
// Strings are localized via ./i18n (English default, RU parity).

import { requestOtp as defaultRequest, verifyOtp as defaultVerify } from '../auth.js';
import { t } from './i18n.js';

interface AuthApi {
  requestOtp(email: string, apiBase: string): Promise<{ ok: boolean; error?: string; retryAfter?: number }>;
  verifyOtp(email: string, code: string, apiBase: string): Promise<{ ok: boolean; error?: string }>;
}

interface LoginGateOpts {
  teamName: string | null;
  apiBase: string;
  onAuthed: () => void;
  /** Injected auth API — defaults to the real requestOtp/verifyOtp. Tests inject mocks. */
  auth?: AuthApi;
  /** 'portal' for team-portal pages (/login, /library, /dashboard, /setlist);
   *  'invite' for the /invite/<token> escape-hatch gate — shows the team name in
   *  invite-flavored copy (not the generic "Private recording" share subtitle,
   *  which would lose the "you're joining a team" context); defaults to 'share'. */
  context?: 'share' | 'portal' | 'invite';
  /** When true, shows the session-expired subtitle instead of the usual one. */
  sessionExpired?: boolean;
}

export function mountLoginGate(host: HTMLElement, opts: LoginGateOpts): void {
  const auth = opts.auth ?? { requestOtp: defaultRequest, verifyOtp: defaultVerify };
  let email = '';

  host.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.className = 'login-gate';

  const title = document.createElement('h2');
  title.textContent = (opts.context === 'portal' || opts.context === 'invite')
    ? t('loginGate.portalTitle')
    : t('loginGate.title');

  const sub = document.createElement('p');
  sub.textContent = opts.context === 'portal'
    ? t('loginGate.portalSub')
    : opts.context === 'invite'
      ? t('loginGate.subInvite', { teamName: opts.teamName ?? '' })
      : opts.teamName
        ? t('loginGate.subWithTeam', { teamName: opts.teamName })
        : t('loginGate.subNoTeam');
  if (opts.sessionExpired) {
    sub.textContent = t('loginGate.sessionExpiredSub');
  }

  wrap.appendChild(title);
  wrap.appendChild(sub);

  const body = document.createElement('div');
  body.className = 'login-gate-body';
  wrap.appendChild(body);

  host.appendChild(wrap);

  function renderNotAllowed(): void {
    title.textContent = t('loginGate.notAllowedTitle');
    sub.textContent = t('loginGate.notAllowedBody');
    const cta = document.createElement('a');
    cta.className = 'gate-cta';
    cta.href = 'https://bandway.app';
    cta.target = '_blank';
    cta.rel = 'noopener';
    cta.textContent = t('loginGate.notAllowedCta');
    body.replaceChildren(cta);
  }

  function renderPending(): void {
    title.textContent = t('loginGate.pendingTitle');
    sub.textContent = t('loginGate.pending');
    body.replaceChildren();
  }

  function renderEmailStep(): void {
    // Real <form> so Enter submits; the submit handler is THE handler (button is
    // type=button + requestSubmit), preventDefault avoids a page reload.
    const form = document.createElement('form');
    form.noValidate = true;

    const label = document.createElement('label');
    label.htmlFor = 'lg-email-input';
    label.textContent = 'Email';

    const input = document.createElement('input');
    input.id = 'lg-email-input';
    input.type = 'email';
    input.inputMode = 'email';
    input.autocomplete = 'email';
    input.setAttribute('autofocus', '');
    input.placeholder = t('loginGate.emailPlaceholder');

    const err = document.createElement('div');
    err.className = 'gate-err';
    err.setAttribute('role', 'alert');
    err.id = 'lg-email-err';
    input.setAttribute('aria-describedby', err.id);

    const btn = document.createElement('button');
    // type=button + click→requestSubmit funnels Enter and click through the single
    // form submit handler (happy-dom only auto-submits connected forms).
    btn.type = 'button';
    btn.dataset.action = 'request';
    btn.textContent = t('loginGate.requestBtn');

    // In-flight guard (B-260724): a double/triple click used to mint several
    // codes seconds apart — with the rate-limit-exempt owner email nothing
    // throttled it, and each mint used to invalidate the previous email's code.
    let inFlight = false;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (inFlight) return;
      inFlight = true;
      btn.disabled = true;
      email = input.value.trim();
      err.textContent = '';
      const r = await auth.requestOtp(email, opts.apiBase);
      inFlight = false;
      btn.disabled = false;
      if (!r.ok) {
        if (r.error === 'rate_limited') {
          const secs = (typeof r.retryAfter === 'number' && r.retryAfter > 0) ? r.retryAfter : 60;
          const wait = secs >= 60
            ? t('common.minutesShort', { n: Math.ceil(secs / 60) })
            : t('common.secondsShort', { n: secs });
          err.textContent = t('loginGate.rateLimitRetry', { wait });
          // Disable the button for secs seconds with a live countdown
          btn.disabled = true;
          let left = secs;
          const tick = setInterval(() => {
            left -= 1;
            if (left <= 0) { clearInterval(tick); btn.disabled = false; }
          }, 1000);
        } else if (r.error === 'pending_approval') {
          renderPending();
        } else if (r.error === 'not_allowed') {
          renderNotAllowed();
        } else {
          err.textContent = t('loginGate.requestError');
        }
        return;
      }
      renderCodeStep();
    });
    btn.addEventListener('click', () => form.requestSubmit());

    label.appendChild(input);
    form.appendChild(label);
    form.appendChild(btn);
    form.appendChild(err);
    body.replaceChildren(form);
    input.focus();
  }

  function renderCodeStep(): void {
    const form = document.createElement('form');
    form.noValidate = true;

    const note = document.createElement('p');
    note.textContent = t('loginGate.codeSent', { email });

    const label = document.createElement('label');
    label.htmlFor = 'lg-code-input';
    label.textContent = t('loginGate.codePlaceholder');

    const code = document.createElement('input');
    code.id = 'lg-code-input';
    code.dataset.field = 'code';
    code.type = 'text';
    code.inputMode = 'numeric';
    code.pattern = '\\d{6}';
    code.autocomplete = 'one-time-code';
    code.maxLength = 6;
    code.setAttribute('autofocus', '');
    code.placeholder = t('loginGate.codePlaceholder');

    const err = document.createElement('div');
    err.className = 'gate-err';
    err.setAttribute('role', 'alert');
    err.id = 'lg-code-err';
    code.setAttribute('aria-describedby', err.id);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.action = 'verify';
    btn.textContent = t('loginGate.verifyBtn');

    // In-flight guard + honest errors (B-260724): every verify failure used to
    // render "Invalid code", masking expired codes and the 5-attempt wipe.
    let inFlight = false;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (inFlight) return;
      inFlight = true;
      btn.disabled = true;
      err.textContent = '';
      const r = await auth.verifyOtp(email, code.value.trim(), opts.apiBase);
      inFlight = false;
      btn.disabled = false;
      if (!r.ok) {
        err.textContent = r.error === 'expired'
          ? t('loginGate.codeExpired')
          : r.error === 'too_many_attempts'
            ? t('loginGate.tooManyAttempts')
            : t('loginGate.invalidCode');
        return;
      }
      opts.onAuthed();
    });
    btn.addEventListener('click', () => form.requestSubmit());

    label.appendChild(code);
    form.appendChild(note);
    form.appendChild(label);
    form.appendChild(btn);
    form.appendChild(err);
    body.replaceChildren(form);
    code.focus();
  }

  renderEmailStep();
}
